import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useActions, useGoals, useOneOnOnes, useOrgMe, useSignalDefinitions, useSignals, useWork, type Person } from "../api/hooks";
import { Chip, Empty, ErrorBox, Loading, Page, Table, statusTone } from "../components/ui";
import { deltaText, latestByScope, toneFor, unitOf } from "../lib/signals";
import { fmtDate, fmtDateTime, relative, title } from "../lib/format";
import { metaFor } from "../lib/connectors";

export const Route = createFileRoute("/people")({ component: People });

// What a manager may see about one direct report: the Management (person-scope) keys the
// nightly writes — work facts, their own meeting hours and after-hours share, and, for
// people who manage or own goals, 1:1 completion, goal health and follow-through.
const personKeys = ["open_work_items", "overdue_work_items", "items_closed_week", "cycle_time_days", "open_work_age_days", "meeting_hours_week", "after_hours_pct", "one_on_one_completion_pct", "goal_health_pct", "action_follow_through_pct"];

function People() {
  const org = useOrgMe();
  const [selected, setSelected] = useState<string | null>(null);
  if (org.isPending) return <Page title="People"><Loading /></Page>;
  if (org.isError) return <Page title="People"><ErrorBox error={org.error} /></Page>;
  if (!org.data.me) return <Page title="People"><Empty title="Your user is not linked to a person" body="Ask an admin to link your sign-in to your HRIS record; then your reports appear here." /></Page>;
  const reports = org.data.direct_reports;
  const current = reports.find((p) => p.person_key === selected) ?? reports[0];
  return (
    <Page title="People" sub={`${reports.length} direct reports. Pick someone for their work, goals, 1:1 history and open actions — the context you bring to the next 1:1.`}>
      {reports.length === 0 ? (
        <Empty title="No direct reports on record" body="Reporting lines come from the HRIS connection or the CSV import." />
      ) : (
        <div className="grid grid-cols-[300px_1fr] gap-4 items-start">
          <div className="card !p-2">
            {reports.map((p) => {
              const active = current?.person_key === p.person_key;
              return (
                <button key={p.person_key} type="button" onClick={() => setSelected(p.person_key)} className={`w-full text-left rounded-xl px-3 py-2.5 mb-0.5 ${active ? "bg-nav-active text-link" : "hover:bg-tile"}`}>
                  <div className="font-semibold text-[13.5px]">{p.full_name ?? p.person_key}</div>
                  <div className="text-[11.5px] text-muted">{p.title ?? "—"}{p.team_key ? ` · ${p.team_key}` : ""}</div>
                </button>
              );
            })}
          </div>
          {current && <Detail person={current} />}
        </div>
      )}
    </Page>
  );
}

function Detail({ person }: { person: Person }) {
  const key = person.person_key;
  const defs = useSignalDefinitions();
  const signals = useSignals({ scope_type: "person", scope_key: key });
  const work = useWork({ open_only: true, limit: 200 });
  const goals = useGoals();
  const ones = useOneOnOnes();
  const actions = useActions({ state: "recommended,awaiting_approval,assigned,accepted,in_progress" });
  const latest = signals.data ? latestByScope(signals.data) : new Map();
  const mine = (work.data ?? []).filter((w) => w.owner_person_key === key);
  const overdue = mine.filter((w) => w.due_at && new Date(w.due_at) < new Date());
  const myGoals = (goals.data ?? []).filter((g) => g.owner_person_key === key || (g.scope_type === "person" && g.scope_key === key));
  const myOnes = (ones.data ?? []).filter((o) => o.report_person_key === key).sort((a, b) => (a.scheduled_at < b.scheduled_at ? 1 : -1));
  const lastDone = myOnes.find((o) => o.status === "completed");
  const next = myOnes.filter((o) => o.status === "scheduled").sort((a, b) => (a.scheduled_at < b.scheduled_at ? -1 : 1))[0];
  const missed = myOnes.filter((o) => o.status === "missed").length;
  const commitments = Array.isArray(lastDone?.commitments) ? (lastDone.commitments as unknown[]) : [];
  const myActions = (actions.data ?? []).filter((a) => a.audience_type === "person" && a.audience_key === key);
  return (
    <div>
      <div className="card mb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-[17px] font-extrabold tracking-tight">{person.full_name ?? key}</h3>
            <div className="text-[12.5px] text-muted mt-0.5">
              {[person.title, person.team_key, person.department, person.location].filter(Boolean).join(" · ") || "—"}
              {person.start_date && ` · since ${fmtDate(person.start_date)}`}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Chip tone={statusTone(person.status)}>{title(person.status)}</Chip>
            <Link to="/one-on-ones" className="link">Prepare a 1:1 →</Link>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3 mt-4">
          {/* Manager-only keys (1:1 completion, goal health, follow-through) only show once they exist. */}
          {personKeys.filter((k, i) => i < 7 || latest.has(`${k}|person|${key}`)).map((k) => {
            const e = latest.get(`${k}|person|${key}`);
            const unit = unitOf(defs.data, k);
            const d = e ? deltaText(k, e.latest, e.previous) : null;
            const tone = e ? toneFor(k, e.latest.value) : "neutral";
            return (
              <div key={k} className="rounded-xl border border-[var(--line)] px-3 py-2.5">
                <div className="text-[11.5px] font-semibold text-muted">{title(k).replace(" Pct", " %")}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[20px] font-extrabold tracking-tight">{e ? `${e.latest.value.toLocaleString(undefined, { maximumFractionDigits: 1 })}${unit === "pct" ? "%" : ""}` : "—"}</span>
                  {e && tone !== "neutral" && <Chip tone={tone}>{tone === "good" ? "Good" : tone === "elevated" ? "Watch" : "High"}</Chip>}
                </div>
                {d && <div className={`delta ${d.cls}`}>{d.text}</div>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="card">
          <h4 className="font-bold text-[13.5px] mb-2">1:1 context</h4>
          <div className="text-[12.5px] leading-6">
            <div><span className="text-muted">Last completed</span> · {lastDone ? fmtDateTime(lastDone.completed_at ?? lastDone.scheduled_at) : "none yet"}</div>
            <div><span className="text-muted">Next scheduled</span> · {next ? fmtDateTime(next.scheduled_at) : "nothing booked"}</div>
            <div><span className="text-muted">Missed (all time)</span> · {missed}</div>
          </div>
          <h5 className="text-[11.5px] font-semibold text-muted uppercase tracking-wide mt-3 mb-1">Carried-over commitments</h5>
          {commitments.length === 0 ? (
            <div className="text-[12.5px] text-muted">None recorded on the last 1:1.</div>
          ) : (
            <ul className="list-disc pl-5 text-[12.5px] leading-5">
              {commitments.map((c, i) => <li key={i}>{typeof c === "string" ? c : typeof c === "object" && c && "text" in c ? String((c as { text: unknown }).text) : JSON.stringify(c)}</li>)}
            </ul>
          )}
        </div>
        <div className="card">
          <h4 className="font-bold text-[13.5px] mb-2">Goals</h4>
          {goals.isPending ? <Loading /> : myGoals.length === 0 ? (
            <div className="text-[12.5px] text-muted">No goals owned by {person.full_name?.split(" ")[0] ?? "them"}. <Link to="/goals" className="link">Set one →</Link></div>
          ) : (
            <ul className="text-[12.5px] leading-6">
              {myGoals.map((g) => (
                <li key={g.id} className="flex items-center justify-between gap-3">
                  <span className="font-semibold truncate">{g.title}</span>
                  <span className="flex items-center gap-2 shrink-0"><span className="text-muted">{Math.round(g.progress_pct)}% · {fmtDate(g.target_date)}</span><Chip tone={statusTone(g.status)}>{title(g.status)}</Chip></span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <h4 className="font-bold text-[13.5px] mt-5 mb-2">Open work{mine.length > 0 && <span className="text-muted font-medium"> · {mine.length} items{overdue.length > 0 ? `, ${overdue.length} overdue` : ""}</span>}</h4>
      {work.isPending ? <Loading /> : (
        <Table
          rows={mine}
          keyOf={(w) => w.item_key}
          empty="Nothing open in the connected work tools"
          cols={[
            { h: "Item", cell: (w) => <><a className="font-semibold" href={w.url ?? undefined} target="_blank" rel="noreferrer">{w.title ?? w.source_id}</a><span className="sub">{metaFor(w.connector).name} · {title(w.kind)}</span></> },
            { h: "Status", cell: (w) => <Chip tone={statusTone(w.outcome ?? w.status)}>{title(w.outcome ?? w.status ?? "open")}</Chip>, w: "130px" },
            { h: "Opened", cell: (w) => <span className="text-muted">{fmtDate(w.opened_at)}</span>, w: "90px" },
            { h: "Due", cell: (w) => { const over = w.due_at && !w.closed_at && new Date(w.due_at) < new Date(); return <span className={over ? "text-red font-semibold" : "text-muted"}>{fmtDate(w.due_at)}</span>; }, w: "90px" },
            { h: "Updated", cell: (w) => <span className="text-muted">{relative(w.updated_at)}</span>, w: "100px" },
          ]}
        />
      )}

      <h4 className="font-bold text-[13.5px] mt-5 mb-2">Open actions about {person.full_name?.split(" ")[0] ?? "them"}</h4>
      {actions.isPending ? <Loading /> : (
        <Table
          rows={myActions}
          keyOf={(a) => a.id}
          empty="No open actions"
          cols={[
            { h: "Action", cell: (a) => <><span className="font-semibold">{a.title}</span>{a.rationale && <span className="sub">{a.rationale}</span>}</> },
            { h: "State", cell: (a) => <Chip tone={statusTone(a.state)}>{title(a.state)}</Chip>, w: "150px" },
            { h: "Due", cell: (a) => <span className="text-muted">{fmtDate(a.due_at)}</span>, w: "90px" },
            { h: "", cell: () => <Link to="/actions" className="link">Open →</Link>, w: "80px" },
          ]}
        />
      )}
    </div>
  );
}
